/*
 * Copyright (C) 2013-2026 Neckar IT GmbH, Mössingen, Germany
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Linking this library statically or dynamically with other modules is
 * making a combined work based on this library. Thus, the terms and
 * conditions of the GNU General Public License cover the whole combination.
 *
 * As a special exception, the copyright holders of this library give you
 * permission to link this library with independent modules, regardless of
 * the license terms of these independent modules, and to copy and distribute
 * the resulting combined work under terms of your choice, provided that every
 * copy of the combined work is accompanied by a complete copy of the source
 * code of this library.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */
package it.neckar.open.version

/**
 * JS implementation: resolves a single git property from the injected deploy metadata.
 *
 * Chain: `globalThis.__APP_GIT_INFO__[propertyKey]` (a plain object filled at serve time —
 * Ktor templating or the nginx entrypoint) → `<meta name="gitHash">` / `<meta
 * name="gitCommitDateTime">` → [VersionInformation.UnknownValue]. Non-browser runtimes (Node,
 * tests) resolve to the fallback without throwing: `globalThis` exists everywhere, `document` is
 * guarded. An injected value of another JS type than a string throws, naming its place.
 *
 * Only hands-off frontends write these tags (lizergy also in an inline script, which is why the
 * global comes first); every other frontend writes `<meta name="imageDigest">` instead.
 */
internal actual fun resolveGitProperty(property: GitProperty): String {
  return findAppGitInfoValue(property.propertyKey)
    ?: findMetaValue(property)
    ?: VersionInformation.UnknownValue
}

/**
 * A page reads `unpinned` and a malformed digest as not injected: it cannot fail its visitor over a diagnosis value.
 */
internal actual fun resolveImageDigest(): String? {
  return findMetaContent(VersionInformation.ImageDigestMetaName)?.takeIf { ImageDigestText.isWellFormed(it) }
}

private fun findAppGitInfoValue(propertyKey: String): String? {
  val appGitInfo: dynamic = js("globalThis.__APP_GIT_INFO__")
  if (appGitInfo == null) {
    return null
  }
  val value: String = injectedString(appGitInfo[propertyKey], "globalThis.__APP_GIT_INFO__.$propertyKey") ?: return null
  return sanitizeInjectedValue(value)
}

/**
 * [raw] as a static String, so Kotlin extensions apply (no smart cast on dynamic); null when it is
 * undefined or null. Any other JS type throws, naming [place] and the type.
 */
private fun injectedString(raw: dynamic, place: String): String? {
  if (raw == null) {
    return null
  }
  return raw as? String ?: error("$place holds ${jsTypeOf(raw)} instead of a string")
}

/**
 * Treats an empty string (envsubst ran without the env var) and an unreplaced `${…}` placeholder
 * (HTML served without the injecting entrypoint, e.g. local webpack dev server) as absent.
 */
private fun sanitizeInjectedValue(value: String): String? {
  return if (value.isEmpty() || value.startsWith("\${")) null else value
}

/**
 * The meta name is the property key: `gitHash` and `gitCommitDateTime` are spelled the same in the
 * HTML head as in the injected global.
 */
private fun findMetaValue(property: GitProperty): String? {
  return findMetaContent(property.propertyKey)
}

private fun findMetaContent(metaName: String): String? {
  val documentOrNull: dynamic = js("typeof document !== 'undefined' ? document : null")
  if (documentOrNull == null) {
    return null
  }

  val metaElement = documentOrNull.querySelector("meta[name='$metaName']")
  if (metaElement == null) {
    return null
  }

  val content: String = injectedString(metaElement.getAttribute("content"), "<meta name=\"$metaName\">") ?: return null
  return sanitizeInjectedValue(content)
}
